import AnimationClip from '../AnimationClip';
import { Easing, Tween } from '@tweenjs/tween.js';
import GameObject from 'GameObject/GameObject';
import { delay } from 'Utility/Task/TaskQueue';
import Point from 'Utility/Point';
import Renderable from 'Component/Renderable';
import { AwaitIterators, PlayAnimation } from '../AnimationHelper';
import { Counter } from './Counter';
import IQuestUpdateTaskData from 'State/Player/Quests/IQuestUpdateTaskData';
import { GLayout } from 'Utility/layout/LayoutHelper';
import { QuestButtonLayout } from 'Layouts/WorldHud/QuestsButtonLayout';
import TransformComponent from 'Component/Transform';
import QuestHelper from 'Utility/QuestHelper';
import TextComponent from 'Component/Render/Text';
import Game from 'Game';
import { MathHelper } from 'Utility/MathHelper';

interface UpdateQuestProgressMeta {
    layout: GLayout<QuestButtonLayout>;
    getNextUpdate: () => IQuestUpdateTaskData;
}

const trayOpenPivot: Point = new Point(0, 0);
export const trayClosedPivot: Point = new Point(-290, 0);
const hideQuestOffset: number = 300;
const progressBarFillMaxWidth: number = 242;

export class UpdateQuestProgress extends AnimationClip<UpdateQuestProgressMeta> {
    private layout: GLayout<QuestButtonLayout>;
    private target: number;

    public *execute(gameObject: GameObject, meta: UpdateQuestProgressMeta): IterableIterator<number> {
        // Store reference to quest button layout.
        this.layout = meta.layout;

        // Get next update data
        let nextUpdateData = meta.getNextUpdate();
        const trayTransform = gameObject.getComponent(TransformComponent);
        // Initial delay
        yield* delay(750);

        // Set initial quest data before showing the tray.
        this.updateQuestProgress(nextUpdateData);

        // Open tray
        yield* this.showTray(trayTransform);

        while (nextUpdateData) {
            const currentData = nextUpdateData;
            nextUpdateData = meta.getNextUpdate();

            const hasNextUpdateData = nextUpdateData !== undefined;

            // Show quest progress update and repeat if there is another quest to update.
            yield* this.showQuestProgressUpdate(currentData, hasNextUpdateData);

            if (hasNextUpdateData) {
                this.updateQuestProgress(nextUpdateData);
            }

            // TODO:
        }

        // Hide tray
        yield* this.showTray(trayTransform, false);
    }

    private createPositionTween(
        transform: TransformComponent,
        to: { x: number; y: number },
        duration: number = 450
    ): Tween {
        return this.getTween(transform.position)
            .to(to, duration)
            .onUpdate(t => {
                try {
                    transform.position = t;
                } catch (e) {
                    console.error(e);
                }
            })
            .easing(Easing.Quadratic.Out);
    }

    private createAlphaTween(renderable: Renderable, to: number, duration: number = 450): Tween {
        return this.getTween(renderable)
            .to({ alpha: to }, duration)
            .easing(Easing.Quadratic.Out);
    }

    private createProgressBarTween(
        progressBar: GLayout<QuestButtonLayout>['progress_bar_fill'],
        endProgress: number,
        duration: number = 450
    ): Tween {
        return this.getTween(progressBar.getComponent(TransformComponent)).to(
            {
                width: this.getWidthFromProgress(endProgress),
            },
            duration
        );
    }

    private *slideQuestObject(
        questObject: GLayout<QuestButtonLayout>['mission_progress_container'],
        targetPosition: { x: number; y: number },
        alpha: number
    ): IterableIterator<number> {
        let questOffsetTween = this.createPositionTween(questObject.getComponent(TransformComponent), targetPosition);
        let questAlphaTween = this.createAlphaTween(questObject.getComponent(Renderable), alpha);
        yield* AwaitIterators([this.startTween(questOffsetTween), this.startTween(questAlphaTween)]);
    }

    /**
     * Gets the width of the progressbar for the given progress
     * @progress base quest progress not % progress
     */
    private getWidthFromProgress(progress: number): number {
        // Clamp `an`d round progress
        const progressInt = Math.floor(MathHelper.clamp(progress, 0, this.target));

        // Get % of bar to fill
        const percProgress = MathHelper.normalize(progressInt, 0, this.target);
        // Interpolate between max and min width based off the % progress to get the new width
        return Math.min(progressBarFillMaxWidth, MathHelper.lerp(0, progressBarFillMaxWidth, percProgress));
    }

    private updateQuestProgress(updateData: IQuestUpdateTaskData) {
        this.target = QuestHelper.getFinalQuestTarget(
            updateData.questData.type,
            updateData.questData.tier,
            QuestHelper.getQuestPoolKeyByEventType(updateData.questData.eventType)
        );

        // Initialize progress container.
        //
        this.layout.progress_bar_title.getComponent(TextComponent).text = Game.locale.getText(
            QuestHelper.getNotificationKey(updateData.questData.type)
        );

        this.layout.progress_bar_text.getComponent(TextComponent).text = `${MathHelper.clamp(
            updateData.questData.progress,
            0,
            this.target
        )} / ${this.target}`;

        this.layout.progress_bar_fill.getComponent(TransformComponent).width = this.getWidthFromProgress(
            updateData.questData.progress
        );
        //
    }

    private *showQuestProgressUpdate(
        updateData: IQuestUpdateTaskData,
        hasMoreUpdates: boolean
    ): IterableIterator<number> {
        // Slide out quest object
        yield* this.slideQuestObject(this.layout.mission_progress_container, { x: 0, y: 0 }, 1);

        let progressBarTween = this.createProgressBarTween(
            this.layout.progress_bar_fill,
            updateData.newProgress + updateData.questData.progress
        );

        yield* delay(350);

        // Fill progress bar + increment text
        yield* AwaitIterators([
            PlayAnimation(
                new Counter(this.layout.progress_bar_text, {
                    startValue: updateData.questData.progress,
                    finalValue: updateData.questData.progress + updateData.newProgress,
                    duration: 450,
                    onUpdateOverride: (count: number) => {
                        const clampedValue = MathHelper.clamp(count, 0, this.target).toFixed(0);

                        this.layout.progress_bar_text.getComponent(
                            TextComponent
                        ).text = `${clampedValue} / ${this.target}`;
                    },
                })
            ),
            this.startTween(progressBarTween),
        ]);

        yield* delay(500);

        if (hasMoreUpdates) {
            // Slide questObject to left and fade out
            yield* this.slideQuestObject(this.layout.mission_progress_container, { x: hideQuestOffset, y: 0 }, 0);
        }
    }

    private *showTray(transform: TransformComponent, show: boolean = true, duration: number = 450) {
        yield* this.startTween(
            this.getTween(transform.pivot)
                .to(show ? trayOpenPivot : trayClosedPivot, duration)
                .easing(Easing.Quadratic.Out)
        );
    }
}
